import React from 'react';
import { Dropdown, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterValue } from 'antd/es/table/interface';
import type { PanelStatus, StatusBucket } from 'types/credentialing';
import dayjs from 'utils/dayjs';
import { STATUS_FILTER_OPTS } from '../../statusMeta';
import StatusBadge from '../StatusBadge';
import s from './s.module.less';

const formatDate = (val?: string) => {
    if (!val) return '-';
    const d = dayjs(val);
    return d.isValid() ? d.format('MM/DD/YYYY') : val;
};

// withdraw 白名单按后端原始状态判定（与后端 withdraw 接口的校验一致；过了 Klarity review 不再是自助场景）；
// Resubmit：仅 withdrawn / panel_closed 行，且后端 payerActive 为 true（payer 仍在接收申请）
const WITHDRAWABLE_API_STATUSES = ['PENDING_PAYMENT', 'KLARITY_REVIEWING', 'WAIT_SUBMIT'];
const RESUBMITTABLE_STATUSES: PanelStatus['status'][] = ['withdrawn', 'panel_closed'];
export const canWithdraw = (r: PanelStatus) => WITHDRAWABLE_API_STATUSES.includes(r.apiStatus || '');
export const canResubmit = (r: PanelStatus) => RESUBMITTABLE_STATUSES.includes(r.status) && r.payerActive === true;

interface Props {
    displayRows: PanelStatus[];
    emptyMsg: string;
    statusFilters: StatusBucket[];
    isMultiState?: boolean;
    onStatusFilterChange: (filters: StatusBucket[]) => void;
    // 不传则不渲染 Actions 列（team provider 只读视图）
    onWithdraw?: (row: PanelStatus) => void;
    onResubmit?: (row: PanelStatus) => void;
}

const SoloCredentialingRecordTable = ({
    displayRows,
    emptyMsg,
    statusFilters,
    isMultiState,
    onStatusFilterChange,
    onWithdraw,
    onResubmit,
}: Props) => {
    const showActions = !!(onWithdraw || onResubmit);
    const columns: ColumnsType<PanelStatus> = [
        {
            title: 'Payer',
            dataIndex: 'name',
            width: '25%',
            render: (name: string, record: PanelStatus) => (
                <span className={s.payerCell}>{isMultiState && record.state ? `${name} - ${record.state}` : name}</span>
            ),
        },
        {
            title: 'Submitted',
            dataIndex: 'submitted',
            width: '25%',
            render: (val: string) => <span className={s.dateCell}>{formatDate(val)}</span>,
        },
        {
            title: 'Last updated',
            dataIndex: 'updated',
            width: '25%',
            render: (val: string) => <span className={s.dateCell}>{formatDate(val)}</span>,
        },
        {
            title: 'Status',
            dataIndex: 'statusBucket',
            width: '25%',
            filters: STATUS_FILTER_OPTS.map((opt) => ({
                text: opt.label,
                value: opt.b,
            })),
            filteredValue: statusFilters.length > 0 ? statusFilters : null,
            onFilter: (value, record) => record.statusBucket === value,
            render: (_: string, record: PanelStatus) => (
                <div className={s.statusCell}>
                    {record.statusDisplay ? (
                        <StatusBadge
                            status={record.status}
                            displayText={record.statusDisplay}
                            contractSigned={record.contractSigned}
                        />
                    ) : (
                        <StatusBadge status={record.status} contractSigned={record.contractSigned} />
                    )}
                    {/* withdrawn 是用户自助撤回（可 Resubmit），不需要联系客服；rejected/paused 才展示 */}
                    {record.statusBucket === 'notapproved' && record.status !== 'withdrawn' && (
                        <a className={s.contactSupport} href="mailto:providersupport@helloklarity.com">
                            Contact support
                        </a>
                    )}
                </div>
            ),
        },
    ];

    // Withdraw/Resubmit 行操作：pending 行 ⋯ 菜单里 Withdraw，withdrawn/panel_closed 且 payerActive 行 Resubmit；其余行为空
    if (showActions) {
        columns.push({
            title: '',
            key: 'actions',
            width: 56,
            render: (_: unknown, record: PanelStatus) => {
                const items = [];
                if (canWithdraw(record) && onWithdraw) {
                    items.push({
                        key: 'withdraw',
                        label: <span style={{ color: '#DC2626' }}>Withdraw</span>,
                        onClick: () => onWithdraw(record),
                    });
                }
                if (canResubmit(record) && onResubmit) {
                    items.push({ key: 'resubmit', label: 'Resubmit', onClick: () => onResubmit(record) });
                }
                if (!items.length) return null;
                return (
                    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
                        <button type="button" className={s.actionsBtn} aria-label="Panel actions">
                            ⋯
                        </button>
                    </Dropdown>
                );
            },
        });
    }

    const handleTableChange = (_pagination: any, filters: Record<string, FilterValue | null>) => {
        const statusValues = (filters.statusBucket || []) as StatusBucket[];
        onStatusFilterChange(statusValues);
    };

    return (
        <div className={s.wrap}>
            <Table<PanelStatus>
                dataSource={displayRows.map(
                    (p, i) => ({ ...p, key: `${p.name}-${i}` }) as PanelStatus & { key: string },
                )}
                columns={columns}
                pagination={false}
                rowClassName={s.dataRow}
                onChange={handleTableChange}
                getPopupContainer={(triggerNode) => triggerNode.closest(`.${s.wrap}`) || document.body}
                locale={{
                    emptyText: <div className={s.empty}>{emptyMsg}</div>,
                }}
            />
        </div>
    );
};

export default SoloCredentialingRecordTable;
